import { styled } from '@mui/material/styles';
import RadioGroup, { RadioGroupProps, useRadioGroup } from '@mui/material/RadioGroup';
import FormControlLabel, {
    FormControlLabelProps,
} from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { SiteEventType, Species, SpeciesForm } from '../../../api/dtos/dtos';
import { Box, Collapse, Typography } from '@mui/material';
import MuiMarkdown from 'mui-markdown';
import { useCallback, useContext, useState } from 'react';

import ToadImage from '../../../assets/images/feral-scan/species/toad/toad_labelled.jpg';
import { SpeciesContext } from '../../../contexts/data/SpeciesContext';
import { useLocation } from 'react-router-dom';

interface StyledFormControlLabelProps extends FormControlLabelProps {
    checked?: boolean;
    imgsrc?: string;
}

const StyledFormControlLabel = styled((props: StyledFormControlLabelProps) => (
    <FormControlLabel {...props} />
))(({ theme, checked }) => ({
    '.MuiFormControlLabel-label': checked && {
        color: theme.palette.primary.main,
    },
}));

function MyFormControlLabel(props: StyledFormControlLabelProps) {
    //
    // -- States --
    const [show, setShow] = useState<boolean>(true);

    //
    // -- Hooks --
    const radioGroup = useRadioGroup();

    let checked = false;

    if (radioGroup) {
        checked = radioGroup.value == props.value;
    }

    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
            e.stopPropagation();
            e.preventDefault();
            setShow(!show);
        },
        [setShow, show]
    );

    return (
        <Box>
            <StyledFormControlLabel 
                checked={checked} 
                {...props}
                label={
                    <Box display="flex">
                        <Typography>
                            <MuiMarkdown>
                                {props.label as string}
                            </MuiMarkdown>
                            { props.imgsrc && (
                                <Typography 
                                    color="primary" 
                                    display="inline" 
                                    onClick={(e) => handleClick(e)}
                                >
                                    &nbsp;(See Image)
                                </Typography>
                            )}
                        </Typography>
                    </Box>
                } 
            />
            { props.imgsrc && (
                <Collapse in={show}>
                    <Box>
                        {props.imgsrc && <img src={props.imgsrc} alt="Related" style={{maxWidth:'300px'}} />}
                    </Box>
                </Collapse>
            )}
        </Box> 
    ); 
}

const imageSources: Record<Species, Record<SiteEventType, string | undefined>> = {
    [Species.Toad]: { [SiteEventType.Sighting]: ToadImage, [SiteEventType.Damage]: undefined, [SiteEventType.Control]: undefined, [SiteEventType.Disease]: undefined, [SiteEventType.Management]: undefined, [SiteEventType.External]: undefined },
    [Species.Rabbit]: { [SiteEventType.Sighting]: undefined, [SiteEventType.Damage]: undefined, [SiteEventType.Control]: undefined, [SiteEventType.Disease]: undefined, [SiteEventType.Management]: undefined, [SiteEventType.External]: undefined },
    [Species.Fox]: { [SiteEventType.Sighting]: undefined, [SiteEventType.Damage]: undefined, [SiteEventType.Control]: undefined, [SiteEventType.Disease]: undefined, [SiteEventType.Management]: undefined, [SiteEventType.External]: undefined },
    [Species.Camel]: { [SiteEventType.Sighting]: undefined, [SiteEventType.Damage]: undefined, [SiteEventType.Control]: undefined, [SiteEventType.Disease]: undefined, [SiteEventType.Management]: undefined, [SiteEventType.External]: undefined },
    [Species.FeralPig]: { [SiteEventType.Sighting]: undefined, [SiteEventType.Damage]: undefined, [SiteEventType.Control]: undefined, [SiteEventType.Disease]: undefined, [SiteEventType.Management]: undefined, [SiteEventType.External]: undefined },
    [Species.Myna]: { [SiteEventType.Sighting]: undefined, [SiteEventType.Damage]: undefined, [SiteEventType.Control]: undefined, [SiteEventType.Disease]: undefined, [SiteEventType.Management]: undefined, [SiteEventType.External]: undefined },
    [Species.WildDog]: { [SiteEventType.Sighting]: undefined, [SiteEventType.Damage]: undefined, [SiteEventType.Control]: undefined, [SiteEventType.Disease]: undefined, [SiteEventType.Management]: undefined, [SiteEventType.External]: undefined },
    [Species.FeralGoat]: { [SiteEventType.Sighting]: undefined, [SiteEventType.Damage]: undefined, [SiteEventType.Control]: undefined, [SiteEventType.Disease]: undefined, [SiteEventType.Management]: undefined, [SiteEventType.External]: undefined },
    [Species.Starling]: { [SiteEventType.Sighting]: undefined, [SiteEventType.Damage]: undefined, [SiteEventType.Control]: undefined, [SiteEventType.Disease]: undefined, [SiteEventType.Management]: undefined, [SiteEventType.External]: undefined },
    [Species.Deer]: { [SiteEventType.Sighting]: undefined, [SiteEventType.Damage]: undefined, [SiteEventType.Control]: undefined, [SiteEventType.Disease]: undefined, [SiteEventType.Management]: undefined, [SiteEventType.External]: undefined },
    [Species.FeralFish]: { [SiteEventType.Sighting]: undefined, [SiteEventType.Damage]: undefined, [SiteEventType.Control]: undefined, [SiteEventType.Disease]: undefined, [SiteEventType.Management]: undefined, [SiteEventType.External]: undefined },
    [Species.Mouse]: { [SiteEventType.Sighting]: undefined, [SiteEventType.Damage]: undefined, [SiteEventType.Control]: undefined, [SiteEventType.Disease]: undefined, [SiteEventType.Management]: undefined, [SiteEventType.External]: undefined },
    [Species.FeralCat]: { [SiteEventType.Sighting]: undefined, [SiteEventType.Damage]: undefined, [SiteEventType.Control]: undefined, [SiteEventType.Disease]: undefined, [SiteEventType.Management]: undefined, [SiteEventType.External]: undefined },
    [Species.NewPests]: { [SiteEventType.Sighting]: undefined, [SiteEventType.Damage]: undefined, [SiteEventType.Control]: undefined, [SiteEventType.Disease]: undefined, [SiteEventType.Management]: undefined, [SiteEventType.External]: undefined },
    [Species.OtherPests]: { [SiteEventType.Sighting]: undefined, [SiteEventType.Damage]: undefined, [SiteEventType.Control]: undefined, [SiteEventType.Disease]: undefined, [SiteEventType.Management]: undefined, [SiteEventType.External]: undefined },
    [Species.None]: { [SiteEventType.Sighting]: undefined, [SiteEventType.Damage]: undefined, [SiteEventType.Control]: undefined, [SiteEventType.Disease]: undefined, [SiteEventType.Management]: undefined, [SiteEventType.External]: undefined }
}

interface SiteEventTypeRadioGroupProps extends RadioGroupProps {
    speciesformdata: SpeciesForm[] | undefined;
}

export default function SiteEventTypeRadioGroup(props: SiteEventTypeRadioGroupProps) {
    //
    // -- Hooks --
    const { species: {species} } = useContext(SpeciesContext);
    const location = useLocation();

    return (
        <RadioGroup 
            name="site-event-type-radio-group"
            defaultValue={undefined}
            {...props}
        >
            {
                location?.pathname?.includes('management') ?
                <>
                    {props.speciesformdata?.find(x => x.title === 'Management') && (
                        <MyFormControlLabel 
                            value={SiteEventType.Management} 
                            label="Management" 
                            control={<Radio />}
                            imgsrc={imageSources[species][SiteEventType.Management]}
                        />
                    )}
                </> : 
                <>
                    {props.speciesformdata?.find(x => x.title === 'Sighting') && (
                        <MyFormControlLabel 
                            value={SiteEventType.Sighting} 
                            label="Sighting" 
                            control={<Radio />} 
                            imgsrc={imageSources[species][SiteEventType.Sighting]}
                        />
                    )}
                    {props.speciesformdata?.find(x => x.title === 'Damage') && (
                        <MyFormControlLabel 
                            value={SiteEventType.Damage} 
                            label="Damage" 
                            control={<Radio />}
                            imgsrc={imageSources[species][SiteEventType.Damage]}
                        />
                    )}
                    {props.speciesformdata?.find(x => x.title === 'Control') && (
                        <MyFormControlLabel 
                            value={SiteEventType.Control} 
                            label="Control" 
                            control={<Radio />}
                            imgsrc={imageSources[species][SiteEventType.Control]}
                        />
                    )}
                    {props.speciesformdata?.find(x => x.title === 'Disease') && (
                        <MyFormControlLabel 
                            value={SiteEventType.Disease} 
                            label="Disease" 
                            control={<Radio />}
                            imgsrc={imageSources[species][SiteEventType.Disease]}
                        />
                    )}
                </>
            }
        </RadioGroup>
    );
}
